import React, { ReactNode } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { List as MovableList, arrayMove } from 'react-movable';
import { CategoryHeader } from 'src/components/CmsCasinoManagement/CategoryHeader/CategoryHeader';
import { GameRow } from 'src/components/CmsCasinoManagement/GameRow/GameRow';
import { DragDrop, CategoryWrapper, GameElement, GameList } from './Category.style';
import { LiveGamesState } from 'src/domains/casino/state/AppCasino/live-games/LiveGames.state';
import { CategoryState } from 'src/domains/casino/state/AppCasino/live-games/Category.state';

interface CategoryPropsType {
    category: CategoryState;
    liveGamesState: LiveGamesState;
}

export const Category = observer('Category', (props: CategoryPropsType) => {
    const { category, liveGamesState } = props;
    const { appCasinoState } = useAppStateContext();
    const { getCategoryPosition } = liveGamesState.collectionData;
    const {
        setGameIds,
        getGameById,
        allowDrop,
        onDropGame,
        checkIfGameMatchAllFilters,
        searchValue,
        isPublishedFilter,
        selectedProviderId,
        selectedStudioId,
        isDragOver,
    } = liveGamesState;

    const categoryPosition = getCategoryPosition(category.id);
    const categoryGames = category.gameIds;

    const onGameClick = (id: number): void => {
        appCasinoState.redirectState.redirectToEditGame(id, liveGamesState.collection);
    };

    //@ts-expect-error
    const gamesList = (children: ReactNode, props): JSX.Element => {
        return category.gameIds.length === 0 ? (
            <DragDrop data-test='drag-and-drop-section'>Drag &amp; Drop games from the right</DragDrop>
        ) : (
            <GameList {...props}>{children}</GameList>
        );
    };

    return (
        <CategoryWrapper
            key={`categoryWrapper-${category.id}`}
            className={category.id.toString()}
            order={categoryPosition}
            onDragOver={allowDrop}
            onDrop={onDropGame}
            isDragOver={isDragOver}
            ref={category.setCategoryRef}
            data-test={`category-id-${category.id}`}
        >
            <CategoryHeader
                category={category}
                liveGamesState={liveGamesState}
            />
            {category.showGames ? (
                <MovableList
                    values={categoryGames}
                    onChange={({ oldIndex, newIndex }): Promise<void> =>
                        setGameIds(category, arrayMove(categoryGames, oldIndex, newIndex))
                    }
                    renderList={({ children, props }): JSX.Element => gamesList(children, props)}
                    renderItem={({ value, props, isDragged }): JSX.Element | null => {
                        if (categoryGames.includes(value) === false) {
                            return null;
                        }

                        const game = getGameById(value);
                        const matchFilter =
                            game !== undefined &&
                            checkIfGameMatchAllFilters(
                                searchValue,
                                isPublishedFilter,
                                selectedProviderId,
                                selectedStudioId,
                                game
                            );

                        if (game !== undefined) {
                            return (
                                <GameElement
                                    isVisible={matchFilter}
                                    {...props}
                                    data-test='game-element'
                                    data-test-visible={matchFilter}
                                >
                                    <GameRow
                                        key={`${category.id}-${game.id}`}
                                        gameModel={game}
                                        isDragged={isDragged}
                                        handleClick={onGameClick}
                                    />
                                </GameElement>
                            );
                        }
                        return null;
                    }}
                />
            ) : null}
        </CategoryWrapper>
    );
});
